import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import { usePage } from "@inertiajs/inertia-vue3";

/**
 * Composable para gerenciar lógica de chat
 * @param {Object} options - Opções de configuração
 * @param {string} options.apiBasePath - Caminho base da API (ex: '/provider/chat')
 * @param {string} options.entityKey - Chave da entidade (ex: 'agency', 'provider')
 * @param {string} options.entityIdKey - Chave do ID da entidade (ex: 'agency_id')
 * @param {Function} options.onMessageReceived - Callback chamado quando uma mensagem é recebida via websocket
 */
export function useChat(options = {}) {
  const {
    apiBasePath,
    entityKey = "agency",
    entityIdKey = "agency_id",
    onMessageReceived = null,
  } = options;

  // Refs
  const conversations = ref([]);
  const selectedConversation = ref(null);
  const messages = ref([]);
  const loadingMessages = ref(false);
  const loadingOlderMessages = ref(false);
  const hasMoreMessages = ref(true);
  const activeChannelId = ref(null);
  const onlineUsers = ref(new Set());
  const presenceChannel = ref(null);

  // Rastreamento de mensagens pendentes para evitar duplicação
  const pendingMessageIds = ref(new Set());
  const pendingMessageTimeouts = ref(new Map());

  // Rastreamento de canais inscritos para evitar múltiplos listeners
  const subscribedChannels = ref(new Set());

  // User data
  const user = computed(() => usePage().props.value.auth.user);
  const provider = computed(() => usePage().props.value.auth.user?.provider);

  // Computed
  const totalUnreadCount = computed(() => {
    return conversations.value.reduce((total, conversation) => {
      return total + (conversation.unread_count || 0);
    }, 0);
  });

  const totalChatsWithUnread = computed(() => {
    return conversations.value.filter(
      (conversation) => conversation.unread_count > 0
    ).length;
  });

  // Methods
  const loadConversations = async () => {
    try {
      const response = await axios.get(`${apiBasePath}/conversations`);
      conversations.value = response.data || [];
      setupGlobalListeners();
      return conversations.value;
    } catch (error) {
      console.error("Erro ao carregar conversas:", error);
      conversations.value = [];
      throw error;
    }
  };

  const selectConversation = async (conversation) => {
    // Limpa canal anterior
    if (activeChannelId.value && window.Echo) {
      try {
        window.Echo.leave(`conversation.${activeChannelId.value}`);
      } catch (e) {}
      activeChannelId.value = null;
    }

    // Limpa IDs temporários muito antigos
    const now = Date.now();
    pendingMessageIds.value.forEach((id) => {
      if (id.startsWith("temp-")) {
        const timestamp = parseInt(id.split("-")[1]);
        if (now - timestamp > 10000) {
          console.log(`[SELECT] Removendo ID temporário antigo: ${id}`);
          pendingMessageIds.value.delete(id);
          const timeoutId = pendingMessageTimeouts.value.get(id);
          if (timeoutId) {
            clearTimeout(timeoutId);
            pendingMessageTimeouts.value.delete(id);
          }
        }
      }
    });

    selectedConversation.value = conversation;
    loadingMessages.value = true;
    hasMoreMessages.value = true;

    try {
      const response = await axios.get(
        `${apiBasePath}/conversations/${conversation.id}/messages`
      );

      if (Array.isArray(response.data)) {
        messages.value = response.data;
        hasMoreMessages.value = false;
      } else if (response.data.messages) {
        messages.value = response.data.messages;
        hasMoreMessages.value = response.data.has_more || false;
      } else {
        messages.value = [];
        hasMoreMessages.value = false;
      }

      await markMessagesAsRead();
      listenForMessages();

      return messages.value;
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
      messages.value = [];
      throw error;
    } finally {
      loadingMessages.value = false;
    }
  };

  const sendMessage = async (messageText, attachments = []) => {
    if (!selectedConversation.value) {
      throw new Error("Nenhuma conversa selecionada");
    }

    const hasText = messageText && messageText.trim().length;
    const hasAttachments = attachments.length > 0;

    if (!hasText && !hasAttachments) {
      throw new Error("Mensagem vazia");
    }

    const formData = new FormData();
    const text = (messageText || "").trim();
    if (text.length) {
      formData.append("message", text);
    }
    attachments.forEach((attachment) => {
      formData.append("attachments[]", attachment.file || attachment);
    });

    try {
      await axios.post(
        `${apiBasePath}/conversations/${selectedConversation.value.id}/messages`,
        formData
      );
      // Após enviar, recarrega todas as mensagens da conversa
      await selectConversation(selectedConversation.value);
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      throw error;
    }
  };

  const markMessageAsRead = async (messageId) => {
    const message = messages.value.find((m) => m.id === messageId);
    if (!message || message.is_mine || message.read_at) return;

    try {
      await axios.post(`${apiBasePath}/messages/${messageId}/read`);
      message.read_at = new Date();

      // Decrementa contador de não lidas
      const conversationIndex = conversations.value.findIndex(
        (c) => c.id === selectedConversation.value?.id
      );
      if (
        conversationIndex !== -1 &&
        conversations.value[conversationIndex].unread_count > 0
      ) {
        conversations.value[conversationIndex].unread_count--;
      }
    } catch (error) {
      console.error("Erro ao marcar mensagem como lida:", error);
    }
  };

  const markMessagesAsRead = async () => {
    if (!selectedConversation.value) return;

    const unreadMessages = messages.value.filter(
      (m) => !m.is_mine && !m.read_at
    );

    // Marca todas as mensagens não lidas
    const promises = unreadMessages.map((message) =>
      markMessageAsRead(message.id)
    );

    await Promise.all(promises);
  };

  const listenForMessages = () => {
    if (!selectedConversation.value || !window.Echo) {
      return;
    }

    const channelName = `conversation.${selectedConversation.value.id}`;

    // Remove listener anterior se existir
    if (activeChannelId.value) {
      const oldChannelName = `conversation.${activeChannelId.value}`;
      window.Echo.leave(oldChannelName);
      subscribedChannels.value.delete(oldChannelName);
    }

    window.Echo.leave(channelName);
    subscribedChannels.value.add(channelName);

    const channel = window.Echo.private(channelName);

    channel.listen(".message.sent", (e) => {
      // O sender_id sempre vem como user.id (da tabela users), nunca como provider.id
      const messageSenderId = e.sender_id || e.sender?.id;
      const myUserId = user.value?.id; // Sempre usar user.id, não provider.id
      const isMyMessage =
        messageSenderId &&
        myUserId &&
        String(messageSenderId) === String(myUserId);

      if (isMyMessage) {
        // Ignora mensagens enviadas por mim (já recarreguei via API)
        return;
      }

      // Se a conversa está aberta, recarrega as mensagens normalmente
      if (
        selectedConversation.value &&
        selectedConversation.value.id === e.conversation_id
      ) {
        selectConversation(selectedConversation.value);
        // Callback para scroll automático
        if (onMessageReceived && typeof onMessageReceived === "function") {
          onMessageReceived(e);
        }
      } else {
        // Se a conversa não está aberta, atualiza unread_count e preview na lista
        const idx = conversations.value.findIndex(
          (c) => c.id === e.conversation_id
        );
        if (idx !== -1) {
          const conversation = conversations.value.splice(idx, 1)[0];
          conversation.unread_count = (conversation.unread_count || 0) + 1;
          conversation.last_message = getMessagePreview(e);
          conversation.last_message_at = e.created_at;
          conversations.value.unshift(conversation);
        } else {
          // Se não existe, adiciona nova conversa ao topo
          conversations.value.unshift({
            id: e.conversation_id,
            unread_count: 1,
            last_message: getMessagePreview(e),
            last_message_at: e.created_at,
            other_user: e.sender, // ou ajuste conforme estrutura
          });
        }
        // Força reatividade para atualizar badge
        conversations.value = [...conversations.value];
      }
    });

    channel.listen(".message.read", (e) => {
      const messageIndex = messages.value.findIndex(
        (m) => m.id === e.message_id
      );
      if (messageIndex !== -1) {
        messages.value[messageIndex].read_at =
          e.read_at || new Date().toISOString();
      }
    });

    activeChannelId.value = selectedConversation.value.id;
  };

  const loadOlderMessages = async () => {
    if (
      !selectedConversation.value ||
      loadingOlderMessages.value ||
      !hasMoreMessages.value
    ) {
      return;
    }

    loadingOlderMessages.value = true;
    const oldestMessageId =
      messages.value.length > 0 ? messages.value[0].id : null;

    if (!oldestMessageId) {
      loadingOlderMessages.value = false;
      hasMoreMessages.value = false;
      return;
    }

    try {
      const response = await axios.get(
        `${apiBasePath}/conversations/${selectedConversation.value.id}/messages`,
        {
          params: { before_message_id: oldestMessageId },
        }
      );

      let olderMessages = [];
      let hasMore = false;

      if (Array.isArray(response.data)) {
        olderMessages = response.data;
        hasMore = false;
      } else if (response.data.messages) {
        olderMessages = response.data.messages;
        hasMore = response.data.has_more || false;
      }

      if (olderMessages.length > 0) {
        messages.value = [...olderMessages, ...messages.value];
        hasMoreMessages.value = hasMore;

        // Marca mensagens antigas não lidas como lidas após um pequeno delay
        setTimeout(() => {
          markMessagesAsRead();
        }, 500);
      } else {
        hasMoreMessages.value = false;
      }

      return { olderMessages, hasMore };
    } catch (error) {
      console.error("Erro ao carregar mensagens antigas:", error);
      throw error;
    } finally {
      loadingOlderMessages.value = false;
    }
  };

  const updateConversationAfterMessage = (message, isMine) => {
    if (!selectedConversation.value) return;

    const idx = conversations.value.findIndex(
      (c) => c.id === selectedConversation.value.id
    );
    if (idx === -1) return;

    const conversation = conversations.value.splice(idx, 1)[0];
    conversation.last_message = getMessagePreview(message);
    conversation.last_message_at = message.created_at;
    if (isMine) {
      conversation.unread_count = 0;
    }

    conversations.value.unshift(conversation);
    selectedConversation.value = conversation;
  };

  const getMessagePreview = (message) => {
    if (!message) return "Sem mensagens";

    const text = message.message && message.message.trim();
    if (text) return text;

    const attachments = Array.isArray(message.attachments)
      ? message.attachments
      : [];
    if (!attachments.length) return "Arquivo enviado";
    if (attachments.length === 1) {
      const firstName = attachments[0]?.name || "";
      return firstName ? `Arquivo: ${firstName}` : "Arquivo enviado";
    }

    return `${attachments.length} arquivos enviados`;
  };

  const clearConversation = () => {
    // Limpa timeouts
    pendingMessageTimeouts.value.forEach((timeoutId) =>
      clearTimeout(timeoutId)
    );
    pendingMessageTimeouts.value.clear();
    pendingMessageIds.value.clear();

    if (activeChannelId.value && window.Echo) {
      const channelName = `conversation.${activeChannelId.value}`;
      window.Echo.leave(channelName);
      subscribedChannels.value.delete(channelName);
    }

    activeChannelId.value = null;
    selectedConversation.value = null;
    messages.value = [];
    loadingMessages.value = false;
    hasMoreMessages.value = true;
    loadingOlderMessages.value = false;
  };

  const setupGlobalListeners = () => {
    if (!window.Echo) {
      return;
    }

    conversations.value.forEach((conversation) => {
      subscribeToConversationUpdates(conversation.id);
    });

    setupPresenceChannel();
    setupUserChannel();
  };

  const setupUserChannel = () => {
    if (!window.Echo || !user.value?.id) return;

    // Escuta notificações de novas conversas
    window.Echo.private(`App.Models.User.${user.value.id}`).listen(
      ".conversation.created",
      (e) => {
        console.log("[USER CHANNEL] Nova conversa criada:", e.conversation);

        // Adiciona a nova conversa no topo da lista
        const existingIndex = conversations.value.findIndex(
          (c) => c.id === e.conversation.id
        );

        if (existingIndex === -1) {
          conversations.value.unshift(e.conversation);

          // Inscreve no canal da nova conversa para receber atualizações
          subscribeToConversationUpdates(e.conversation.id);
        }
      }
    );
  };

  const setupPresenceChannel = () => {
    if (!window.Echo) return;

    presenceChannel.value = window.Echo.join("online")
      .here((users) => {
        onlineUsers.value = new Set(users.map((u) => u.id));
        updateConversationsStatus();
      })
      .joining((user) => {
        onlineUsers.value.add(user.id);
        updateConversationsStatus();
      })
      .leaving((user) => {
        onlineUsers.value.delete(user.id);
        updateConversationsStatus();
      })
      .error((error) => {
        console.error("[PRESENCE] Erro no canal de presença:", error);
      });
  };

  const updateConversationsStatus = () => {
    conversations.value.forEach((conversation) => {
      // Tenta usar other_user primeiro (estrutura nova)
      let entity = conversation.other_user;
      let userId = entity?.user_id || entity?.id;

      // Fallback para entityKey (estrutura antiga)
      if (!userId && conversation[entityKey]) {
        entity = conversation[entityKey];
        userId = entity.user_id;
      }

      if (userId) {
        const wasOnline = entity.is_online;
        const isOnline = onlineUsers.value.has(userId);

        if (wasOnline !== isOnline) {
          entity.is_online = isOnline;
        }
      }
    });
  };

  const subscribeToConversationUpdates = (conversationId) => {
    if (!window.Echo) return;

    // Não inscrever se for a conversa ativa (já tem listener em listenForMessages)
    if (activeChannelId.value === conversationId) return;

    const channelName = `conversation.${conversationId}`;

    // Não inscrever se já estiver inscrito
    if (subscribedChannels.value.has(channelName)) return;

    subscribedChannels.value.add(channelName);

    window.Echo.private(channelName).listen(".message.sent", (e) => {
      // Atualiza apenas o contador e timestamp, não adiciona a mensagem
      const conversation = conversations.value.find(
        (c) => c.id === conversationId
      );
      if (conversation) {
        conversation.unread_count = (conversation.unread_count || 0) + 1;
        conversation.last_message_at = e.created_at;

        // Move conversa para o topo
        const index = conversations.value.indexOf(conversation);
        if (index > 0) {
          conversations.value.splice(index, 1);
          conversations.value.unshift(conversation);
        }
      }
    });
  };

  const removeGlobalListeners = () => {
    if (!window.Echo) return;

    if (presenceChannel.value) {
      window.Echo.leave("online");
      presenceChannel.value = null;
    }

    // Remove canal do usuário
    if (user.value?.id) {
      window.Echo.leave(`App.Models.User.${user.value.id}`);
    }

    // Remove todos os canais inscritos
    subscribedChannels.value.forEach((channelName) => {
      window.Echo.leave(channelName);
    });
    subscribedChannels.value.clear();

    // Remove canal ativo
    if (activeChannelId.value) {
      window.Echo.leave(`conversation.${activeChannelId.value}`);
      activeChannelId.value = null;
    }
  };

  const isConversationOnline = (conversation) => {
    if (!conversation) return false;

    // Tenta usar other_user primeiro (estrutura nova)
    let entity = conversation.other_user;

    // Fallback para entityKey (estrutura antiga)
    if (!entity && conversation[entityKey]) {
      entity = conversation[entityKey];
    }

    if (!entity) return false;

    const userId = entity.user_id || entity.id;
    if (userId && onlineUsers.value.has(userId)) {
      return true;
    }

    return entity.is_online === true;
  };

  const getConversationName = (conversation) => {
    if (!conversation) return "Desconhecido";

    // Tenta usar other_user primeiro (estrutura nova)
    if (conversation.other_user) {
      return conversation.other_user.name || "Desconhecido";
    }

    // Fallback para entityKey (estrutura antiga)
    if (conversation[entityKey]) {
      return conversation[entityKey].name || "Desconhecido";
    }

    return "Desconhecido";
  };

  const getConversationInfo = (conversation) => {
    if (!conversation) return "";

    // Tenta usar other_user primeiro (estrutura nova)
    if (conversation.other_user) {
      return conversation.other_user.cnpj
        ? `CNPJ: ${conversation.other_user.cnpj}`
        : "";
    }

    // Fallback para entityKey (estrutura antiga)
    if (conversation[entityKey]) {
      const entity = conversation[entityKey];
      return entity.cnpj ? `CNPJ: ${entity.cnpj}` : "";
    }

    return "";
  };

  // Funções utilitárias que estavam faltando no return
  const resetOnlineStatus = () => {
    onlineUsers.value = new Set();
    conversations.value.forEach((conversation) => {
      let entity = conversation.other_user;
      if (!entity && conversation[entityKey]) {
        entity = conversation[entityKey];
      }
      if (entity) {
        entity.is_online = false;
      }
    });
  };

  // Lifecycle
  onBeforeUnmount(() => {
    removeGlobalListeners();
    if (activeChannelId.value && window.Echo) {
      window.Echo.leave(`conversation.${activeChannelId.value}`);
    }
    pendingMessageTimeouts.value.forEach((timeoutId) =>
      clearTimeout(timeoutId)
    );
    pendingMessageTimeouts.value.clear();
    pendingMessageIds.value.clear();
  });

  return {
    // State
    conversations,
    selectedConversation,
    messages,
    loadingMessages,
    loadingOlderMessages,
    hasMoreMessages,
    onlineUsers,

    // Computed
    totalUnreadCount,
    totalChatsWithUnread,

    // Methods
    loadConversations,
    selectConversation,
    sendMessage,
    markMessagesAsRead,
    markMessageAsRead,
    loadOlderMessages,
    clearConversation,
    isConversationOnline,
    getConversationName,
    getConversationInfo,
    getMessagePreview,
    setupGlobalListeners,
    removeGlobalListeners,
    resetOnlineStatus,
  };
}
